"use client";

/**
 * Everything that happens when a visitor clicks through to app / hrms / assessments.
 *
 * Two jobs, one listener:
 *
 * 1. CARRY THE VISIT IDENTITY across the domain boundary (hb_sid + hb_vid + the
 *    first-touch UTMs). Without it a registration in a product app can never be
 *    joined to the visit that produced it -- hb_sid specifically, because
 *    signup_events has session_id and no visitor_id, and that is the column every
 *    funnel RPC joins on.
 *
 * 2. RECORD THE CLICK as a CTA event. The pricing pages carry 17 product links
 *    across three components (RecruitmentPricing, HRMSPricing, AssessmentPricing)
 *    and not one of them fired an event -- the highest-intent click on the site was
 *    invisible, which is why "pricing" never appeared in the CTA panel.
 *
 * Done centrally rather than as 17 onClick handlers: it covers every product anchor
 * that exists today and any added later, with no chance of someone forgetting. The
 * one component that already tracks its product links (ProductSelectionModal) uses a
 * <button> with window.open rather than an <a href>, so it is not matched here and
 * cannot double-count -- it calls decorateWithVisitIdentity itself for job 1.
 *
 * Two listeners, because the jobs need different phases. The href rewrite is in the
 * capture phase so it lands before the browser reads the href. The recording is in
 * the bubble phase, after every React onClick: a hero button whose CMS link points at
 * a product site has its own trackCtaClick, and recording it here first (capture)
 * counted that click twice. wasClickTracked() now lets the explicit handler win.
 *
 * Click time, not render time: the URL builders run inside href={...} during SSR,
 * where there is no localStorage, and a server/client difference in the href is a
 * hydration mismatch. Rewriting href in a capture listener is read by the browser
 * after the listener chain, so the navigation uses the decorated value.
 */

import { useEffect } from "react";
import { isProductUrl, productLinkLabel, resolveRawHref } from "@/lib/productDomains";
import { decorateWithVisitIdentity } from "@/lib/visitorIdentity";
import { trackCtaClick, wasClickTracked } from "@/utils/ctaTracking";

/** Plan tier from the link, so the panel separates Starter/Pro/Enterprise. */
function planFromUrl(url: string): string | null {
  try {
    return new URL(url, window.location.origin).searchParams.get("plan");
  } catch {
    return null;
  }
}

function productAnchor(e: MouseEvent): HTMLAnchorElement | null {
  const el = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
  const href = el?.getAttribute("href");
  return el && href && isProductUrl(href) ? el : null;
}

export function useProductLinkTracking(): void {
  useEffect(() => {
    // Per anchor: the href as written (raw) and the one we wrote over it (decorated).
    // raw keeps the recorded destination and plan free of this visitor's ids; decorated
    // tells our own rewrite apart from React patching in a new URL, which then becomes
    // the raw value (see resolveRawHref).
    const original = new WeakMap<HTMLAnchorElement, { raw: string; decorated: string }>();
    const rawHref = (el: HTMLAnchorElement) => resolveRawHref(original.get(el), el.getAttribute("href")!);
    const applyDecoration = (el: HTMLAnchorElement, raw: string) => {
      const decorated = decorateWithVisitIdentity(raw);
      if (decorated !== el.getAttribute("href")) el.setAttribute("href", decorated);
      original.set(el, { raw, decorated });
    };

    // Identity for every product link, including the banner buttons and explicitly
    // tracked buttons that `record` skips. Always decorated from the original href, so
    // a second click after the session rolled over carries the new id, not the old one.
    const decorate = (e: MouseEvent) => {
      const el = productAnchor(e);
      if (!el) return;
      applyDecoration(el, rawHref(el));
    };

    // A banner's button is recorded by useCtaBannerTracking (capture), and an explicit
    // onClick by itself; both set wasClickTracked().
    const record = (e: MouseEvent) => {
      if (wasClickTracked()) return;
      const el = productAnchor(e);
      if (!el) return;
      const raw = rawHref(el);
      trackCtaClick(
        raw,
        // The panel groups by action, so product and plan have to be in the name or
        // every tier -- of every product -- collapses into one "Get Started" row.
        productLinkLabel(el.textContent || "", raw),
        undefined,
        { section: "product_link", location: planFromUrl(raw) ?? "none" },
      );
      // A visitor whose very first act is this click had no session to carry during
      // capture; it exists now, so decorate again before the browser navigates.
      // (Default action runs after dispatch, so the href read happens after this.)
      applyDecoration(el, raw);
    };

    document.addEventListener("click", decorate, true);
    document.addEventListener("click", record);
    return () => {
      document.removeEventListener("click", decorate, true);
      document.removeEventListener("click", record);
    };
  }, []);
}
